import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/useToast";
import { eventsApi } from "@/lib/humhub-api/events";
import type { RecurrenceScope } from "@/lib/humhub-api/types";

interface UsePropagateRemindersOptions {
  onSuccess?: () => void;
  onError?: () => void;
}

interface UsePropagateRemindersReturn {
  isLoading: boolean;
  error: Error | null;
  propagateReminders: (
    eventId: number,
    scope?: RecurrenceScope,
  ) => Promise<void>;
}

export const usePropagateReminders = (
  options?: UsePropagateRemindersOptions,
): UsePropagateRemindersReturn => {
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async ({
      eventId,
      scope,
    }: {
      eventId: number;
      scope?: RecurrenceScope;
    }) => {
      return eventsApi.propagateEventReminders(eventId, scope);
    },
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error) => {
      console.error("Error propagating reminders:", error);
      options?.onError?.();
      toast({
        title: t("common.error"),
        description: t(
          "eventManage.reminderPropagationFailed",
          "Failed to propagate reminders",
        ),
        variant: "error",
      });
    },
  });

  const propagateReminders = useCallback(
    async (eventId: number, scope: RecurrenceScope = "all"): Promise<void> => {
      await mutation.mutateAsync({ eventId, scope });
    },
    [mutation],
  );

  return {
    isLoading: mutation.isPending,
    error: mutation.error,
    propagateReminders,
  };
};
